import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import ChatInput from "./components/ChatInput";
import ChatMessage from "./components/ChatMessage";
import Header from "./components/Header";
import ModeSelector from "./components/ModeSelector";
import KnowledgeBaseManager from "./components/KnowledgeBaseManager";

// Backend API base URL (production mirror)
const API_BASE_URL = "https://internal-api.profitwheel.com/api";

const DEFAULT_MODE_KEY = "customer_success_manager";

// Fallback modes (embed MODES_CONFIG from llm_service)
const FALLBACK_MODES = {
  customer_success_manager: {
    display_name: "Customer Success Manager",
    greeting:
      "Hello Customer Success Manager 👋\nPlease provide the client's current situation, challenges, and any relevant context. I'll use that to guide you in ensuring client satisfaction and retention.",
  },
  sales_manager: {
    display_name: "Sales Manager",
    greeting:
      "Hello Sales Manager 👋\nPlease share the client profile, and any objections or deal stage details. I'll assist you in crafting pitches and strategies to close deals.",
  },
  product_manager: {
    display_name: "Product Manager",
    greeting:
      "Hello Product Manager 👋\nPlease describe the product requirements, user feedback, or roadmap concerns you have. I'll help you prioritize features and define product strategy.",
  },
  marketing_manager: {
    display_name: "Marketing Manager",
    greeting:
      "Hello Marketing Manager 👋\nPlease share campaign goals, target audience, and any brand guidelines or metrics you have. I'll help you craft effective marketing strategies.",
  },
  client_partner: {
    display_name: "Client Partner",
    greeting:
      "Hello Client Partner 👋\nPlease describe the client engagement details, objectives, and stakeholder information. I'll help you manage relationships and align solutions with client needs.",
  },
};

function App() {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const [modes, setModes] = useState({});
  const [currentMode, setCurrentMode] = useState(DEFAULT_MODE_KEY);
  const [isLoadingModes, setIsLoadingModes] = useState(true);
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);

  // Scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    const storedSessionId = localStorage.getItem("chatSessionId");
    if (storedSessionId) {
      setSessionId(storedSessionId);
    }
  }, []);

  // Fetch modes from backend via same-origin proxy
  useEffect(() => {
    setIsLoadingModes(true);
    fetch(`${API_BASE_URL}/modes`)
      .then((res) => {
        if (!res.ok) throw new Error(res.statusText);
        return res.json();
      })
      .then((data) => {
        setModes(data);
        const firstMode = Object.keys(data)[0] || DEFAULT_MODE_KEY;
        setCurrentMode(firstMode);
        setMessages([{ role: "assistant", content: data[firstMode].greeting }]);
      })
      .catch((err) => {
        console.error("Error fetching modes:", err);
        setModes(FALLBACK_MODES);
        setCurrentMode(DEFAULT_MODE_KEY);
        setMessages([
          {
            role: "assistant",
            content: FALLBACK_MODES[DEFAULT_MODE_KEY].greeting,
          },
        ]);
      })
      .finally(() => setIsLoadingModes(false));
  }, []);

  useEffect(() => {
    try {
      fetch(`${API_BASE_URL}/system_prompt?mode=${currentMode}`)
        .then((res) => res.json())
        .then((data) => console.log("DEBUG system_prompt:", data.system_prompt))
        .catch((e) => console.error("DEBUG system_prompt error:", e));
    } catch (e) {
      console.error("DEBUG system_prompt unexpected error:", e);
    }
  }, [currentMode]);

  const toggleWebSearch = () => {
    setWebSearchEnabled((prev) => !prev);
  };

  const sendMessage = async (message) => {
    if (!message.trim()) return;

    // Add user message to chat
    setMessages((prev) => [...prev, { role: "user", content: message }]);

    // Set loading state
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          message,
          session_id: sessionId,
          mode: currentMode, // Send current mode
          web_search: webSearchEnabled,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Create a temporary variable to store the assistant's message as it streams in
      let assistantMessage = "";
      let sessionIdFromStream = null;

      // Get a reader from the response body stream
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      // Read the stream
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk and process it
        const chunk = decoder.decode(value);
        const lines = chunk
          .split("\n")
          .filter((line) => line.trim().startsWith("data: "));

        for (const line of lines) {
          try {
            // Parse the JSON data (remove the 'data: ' prefix)
            const jsonData = JSON.parse(line.substring(6));

            if (jsonData.chunk) {
              // Append the chunk to the assistant message
              assistantMessage += jsonData.chunk;

              // Update the UI with the current progress
              setMessages((prev) => {
                const newMessages = [...prev];
                // If the last message is from the assistant, update it
                if (
                  newMessages.length > 0 &&
                  newMessages[newMessages.length - 1].role === "assistant"
                ) {
                  newMessages[newMessages.length - 1].content =
                    assistantMessage;
                } else {
                  // Otherwise add a new assistant message
                  newMessages.push({
                    role: "assistant",
                    content: assistantMessage,
                  });
                }
                return newMessages;
              });
            }

            if (jsonData.done && jsonData.session_id) {
              sessionIdFromStream = jsonData.session_id;
            }
          } catch (e) {
            console.error("Error parsing stream chunk:", e, line);
          }
        }
      }

      // Save session ID if received
      if (sessionIdFromStream) {
        setSessionId(sessionIdFromStream);
        localStorage.setItem("chatSessionId", sessionIdFromStream);
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I'm sorry, there was an error processing your request. Please try again later.",
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleModeChange = (newModeKey) => {
    if (modes[newModeKey]) {
      setCurrentMode(newModeKey);
      setMessages([
        {
          role: "assistant",
          content:
            modes[newModeKey].greeting ||
            "Hello! How can I assist you in this role?",
        },
      ]);
      // Optionally, reset session ID or perform other actions
      // setSessionId(null);
      // localStorage.removeItem("chatSessionId");
    } else {
      console.warn(`Attempted to switch to an unknown mode: ${newModeKey}`);
    }
  };

  const resetConversation = async () => {
    try {
      await fetch(`${API_BASE_URL}/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          session_id: sessionId,
        }),
      });

      // Clear messages and add greeting for current mode
      if (modes[currentMode] && modes[currentMode].greeting) {
        setMessages([
          {
            role: "assistant",
            content: modes[currentMode].greeting,
          },
        ]);
      } else {
        setMessages([
          {
            role: "assistant",
            content: "Hello! How can I help you today?", // Fallback greeting
          },
        ]);
      }
    } catch (error) {
      console.error("Error resetting conversation:", error);
    }
  };

  return (
    <div className="app">
      <Header onReset={resetConversation} />
      <ModeSelector
        modes={modes}
        currentMode={currentMode}
        onModeChange={handleModeChange}
        disabled={loading || isLoadingModes}
      />
      <KnowledgeBaseManager />
      <div className="chat-container">
        <div className="messages-container">
          {messages.map((message, index) => (
            <ChatMessage
              key={index}
              role={message.role}
              content={message.content}
            />
          ))}
          {loading && (
            <div className="loading-indicator">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        <ChatInput
          onSendMessage={sendMessage}
          disabled={loading || isLoadingModes}
          webSearchEnabled={webSearchEnabled}
          onToggleWebSearch={toggleWebSearch}
        />
      </div>
    </div>
  );
}

export default App;
